import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AlertType } from "_shared/_views/system-notification-banner/system-notification-banner";

export interface SystemNotificationBannerContextProps {
  message: string | null;
  type: AlertType | null;
  setBanner: (type: AlertType | null, message: string) => void;
  clearBanner: () => void;
}

const SystemNotificationBannerContext = createContext<SystemNotificationBannerContextProps | undefined>(undefined);

let globalSetBanner: SystemNotificationBannerContextProps["setBanner"] | null = null;

export function getGlobalSystemNotificationBannerSetter() {
  return globalSetBanner;
}

export function setSystemNotificationBanner(type: AlertType | null, message: string) {
  if (globalSetBanner) {
    globalSetBanner(type, message);
  } else if (process.env.NODE_ENV !== 'production') {
    console.warn('SystemNotificationBannerProvider is not mounted yet.');
  }
}

export function clearSystemNotificationBanner() {
  if (globalSetBanner) {
    globalSetBanner(null, "");
  } else if (process.env.NODE_ENV !== 'production') {
    console.warn('SystemNotificationBannerProvider is not mounted yet.');
  }
}

export const SystemNotificationBannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [type, setType] = useState<AlertType | null>(null);

  const clearBanner = useCallback(() => {
    setMessage(null);
    setType(null);
  }, []);

  const setBanner = useCallback((type: AlertType | null, message: string) => {
    setType(type);
    setMessage(message);
  }, []);

  useEffect(() => {
    globalSetBanner = setBanner;
    return () => {
      if (globalSetBanner === setBanner) globalSetBanner = null;
    };
  }, [setBanner]);

  const contextValue = React.useMemo(
    () => ({ message, type, setBanner, clearBanner }),
    [message, type, setBanner, clearBanner]
  );

  return (
    <SystemNotificationBannerContext.Provider value={contextValue}>
      {children}
    </SystemNotificationBannerContext.Provider>
  );
};

export function useSystemNotificationBanner() {
  const ctx = useContext(SystemNotificationBannerContext);
  if (!ctx) throw new Error("useSystemNotificationBanner must be used within a SystemNotificationBannerProvider");
  return ctx;
}
